import React, { useState, useEffect, useRef, useCallback } from 'react'
import WisdomGame from './components/WisdomGame'
import CompletionPanel from './components/CompletionPanel'
import remoteLog from '../../shared/remoteLog'
import * as store from './wisdomStore'
import './WisdomLearning.css'

const FLUSH_INTERVAL_MS = 10000

// Pick the next non-mastered wisdom, starting at `fromIndex` and cycling.
function pickNextWisdom(state, fromIndex) {
  if (!state || state.wisdoms.length === 0) return null
  const mastered = new Set(store.masteredWisdomIds(state))
  for (let i = 0; i < state.wisdoms.length; i++) {
    const candidate = state.wisdoms[(fromIndex + i) % state.wisdoms.length]
    if (!mastered.has(candidate.id)) return candidate
  }
  return null
}

export default function WisdomLearning({ activityId, sessionId, railsEnv, onComplete }) {
  const mountTime = useRef(Date.now())
  const [isLoading, setIsLoading] = useState(true)
  const [state, setState] = useState(null)
  // Which wisdom is currently rendered. Only advances when the kid clicks Next,
  // NOT when the store marks a wisdom as mastered — otherwise the correct/quiz
  // screen would disappear immediately on answer_correctly.
  const [currentWisdomId, setCurrentWisdomId] = useState(null)
  const [currentWisdomIndex, setCurrentWisdomIndex] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const [error, setError] = useState(null)

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
    if (state) store.save(sessionId, state)
  }, [state, sessionId])

  const updateState = useCallback((updater) => {
    setState((prev) => (prev ? updater(prev) : prev))
  }, [])

  const flushNow = useCallback(async () => {
    const current = stateRef.current
    if (!current || !current.pendingEvents.length) return
    const { ok, applyAck } = await store.flush(sessionId, current)
    if (ok) setState((s) => (s ? applyAck(s) : s))
  }, [sessionId])

  useEffect(() => {
    remoteLog.info('WisdomLearning MOUNTED', { activityId, sessionId, railsEnv })
    return () => {
      remoteLog.info('WisdomLearning UNMOUNTED', { sessionId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load: prefer localStorage, fall back to server fetch.
  useEffect(() => {
    let cancelled = false

    const initialize = async () => {
      const cached = store.load(sessionId)
      if (cached && cached.wisdoms.length > 0) {
        if (cancelled) return
        setState(cached)
        const first = pickNextWisdom(cached, 0)
        setCurrentWisdomId(first?.id ?? null)
        setIsLoading(false)
        if (cached.pendingEvents.length > 0) {
          store.flush(sessionId, cached).then(({ ok, applyAck }) => {
            if (ok && !cancelled) setState((s) => (s ? applyAck(s) : s))
          })
        }
        return
      }

      try {
        const data = await store.fetchRandom(sessionId)
        if (cancelled) return
        const fresh = store.hydrateFromServer(sessionId, data)
        setState(fresh)
        const first = pickNextWisdom(fresh, 0)
        setCurrentWisdomId(first?.id ?? null)
        setIsLoading(false)
      } catch (err) {
        remoteLog.error('WisdomLearning: Error loading wisdoms', { error: err.message })
        if (!cancelled) {
          setError('Không thể tải câu tục ngữ. Vui lòng thử lại.')
          setIsLoading(false)
        }
      }
    }

    initialize()
    return () => {
      cancelled = true
    }
  }, [sessionId])

  // Background sync: periodic + visibility change + pagehide beacon.
  useEffect(() => {
    if (!sessionId) return undefined

    const interval = setInterval(() => {
      flushNow()
    }, FLUSH_INTERVAL_MS)

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') flushNow()
    }
    const handlePageHide = () => {
      const current = stateRef.current
      if (!current) return
      const next = store.flushBeacon(sessionId, current)
      if (next !== current) {
        setState(next)
        store.save(sessionId, next)
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('beforeunload', handlePageHide)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('beforeunload', handlePageHide)
    }
  }, [sessionId, flushNow])

  const pushEvent = useCallback((event) => {
    updateState((prev) => store.pushEvent(prev, event))
  }, [updateState])

  // Anti-cheat for the two-tab race: use_hint MUST be durable at the server
  // before the hint UI reveals the answer, otherwise another tab could race an
  // answer_correctly through without the penalty. This is the only event we
  // block on — normal play stays fire-and-forget.
  //
  // We compute nextState explicitly and pass it to store.flush so the POST
  // includes the event we just pushed. Using flushNow here would read
  // stateRef.current, which lags behind setState until the commit useEffect
  // runs — the freshly-pushed event would miss the flush.
  const pushPenaltyEvent = useCallback(async (event) => {
    const current = stateRef.current
    if (!current) return
    const next = store.pushEvent(current, event)
    setState(next)
    const { ok, applyAck } = await store.flush(sessionId, next)
    if (ok) setState((s) => (s ? applyAck(s) : s))
  }, [sessionId])

  const handleWisdomComplete = (_result) => {
    // Read via functional setState so we see the very latest state, including
    // any setState from pushEvent calls that fired in the same event tick.
    setState((current) => {
      if (!current) return current
      const newTotal = store.totalScore(current)
      const nextIndex = currentWisdomIndex + 1
      if (newTotal >= current.targetScore) {
        setIsCompleted(true)
      } else {
        const next = pickNextWisdom(current, nextIndex)
        setCurrentWisdomIndex(nextIndex)
        setCurrentWisdomId(next?.id ?? null)
        if (!next) setIsCompleted(true)
      }
      return current
    })
  }

  const handleComplete = async () => {
    await flushNow()
    if (onComplete) {
      const current = stateRef.current
      onComplete({
        total_score: current ? store.totalScore(current) : 0,
        wisdoms: current ? store.completedWisdoms(current) : [],
        mastered_wisdom_ids: current ? store.masteredWisdomIds(current) : []
      })
    }
    store.clear(sessionId)
  }

  // Test helper (Cucumber + unit tests).
  useEffect(() => {
    if (typeof window === 'undefined' || railsEnv !== 'test') return undefined

    const currentWisdomFromId = () => {
      const s = stateRef.current
      if (!s || !currentWisdomId) return null
      return s.wisdoms.find((w) => w.id === currentWisdomId) || null
    }

    window.__WISDOM_TEST_HELPER__ = {
      completeCurrentWisdom: (useHints = false) => {
        const current = currentWisdomFromId()
        if (!current) return
        pushEvent({ wisdom_id: current.id, event: 'asked' })
        if (useHints) pushEvent({ wisdom_id: current.id, event: 'use_hint' })
        pushEvent({ wisdom_id: current.id, event: 'answer_correctly' })
        handleWisdomComplete({
          wisdomId: current.id,
          score: useHints ? 0 : 1,
          hintsUsed: useHints ? 1 : 0,
          mastered: !useHints
        })
      },
      getTotalScore: () => (stateRef.current ? store.totalScore(stateRef.current) : 0),
      getTargetScore: () => stateRef.current?.targetScore || 0,
      getCurrentWisdom: currentWisdomFromId
    }

    return () => {
      delete window.__WISDOM_TEST_HELPER__
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railsEnv, currentWisdomId])

  if (isLoading) {
    return (
      <div className="wisdom-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Đang tải...</span>
        </div>
        <p className="mt-3">Đang tải câu tục ngữ...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="wisdom-error">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    )
  }

  const totalScore = state ? store.totalScore(state) : 0
  const targetScore = state?.targetScore || 0

  if (isCompleted) {
    return <CompletionPanel totalScore={totalScore} onComplete={handleComplete} />
  }

  const wisdom = state && currentWisdomId
    ? state.wisdoms.find((w) => w.id === currentWisdomId)
    : null

  if (!wisdom) {
    return <CompletionPanel totalScore={totalScore} onComplete={handleComplete} />
  }

  return (
    <div className="wisdom-learning">
      <WisdomGame
        key={`wisdom-${currentWisdomIndex}-${wisdom.id}`}
        wisdom={wisdom}
        totalScore={totalScore}
        targetScore={targetScore}
        // Persisted hint flag from the store — survives reloads. Without this,
        // a kid could reveal words with a hint, reload to reset local state,
        // and score 1 by re-arranging (they already know the answer).
        alreadyPenalized={!!state?.wisdomState?.[wisdom.id]?.hintUsed}
        onComplete={handleWisdomComplete}
        onEvent={pushEvent}
        onPenaltyEvent={pushPenaltyEvent}
        railsEnv={railsEnv}
      />
    </div>
  )
}
